import { ApiTransport } from "../../../network/ApiTransport";
import { MockApiTransport } from "../../../network/MockApiTransport";
import { configHelper } from "../../../config/ConfigHelper";
import { parseNumericCsv } from "../../../network/parseNumericCsv";
import type { IApiTransport } from "../../../definitions/IApiTransport";
import type { SpreadPoint } from "../definitions/SpreadPoint";
import { mockSpreadData } from "../mocks/spreadData.fixture";

/** `?mock=1` swaps in `MockApiTransport` so the dashboard works offline / while the real API is unavailable. */
function isMockEnabled(): boolean {
  return new URLSearchParams(window.location.search).get("mock") === "1";
}

const mockEnabled = isMockEnabled();
if (mockEnabled) {
  console.info("[SpreadDataService] mock=1 — serving spread data from local fixture.");
}

const apiTransport: IApiTransport = mockEnabled
  ? new MockApiTransport(mockSpreadData)
  : new ApiTransport();

function isSpreadPoint(value: unknown): value is SpreadPoint {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    typeof value[0] === "number" &&
    typeof value[1] === "number"
  );
}

/** The real chartv6 API's response envelope: `{ data: [{ DT: epochMs, Spread: number }, ...], ... }`. */
interface ChartV6Response {
  data: Array<{ DT: number; Spread: number }>;
}

function isChartV6Response(value: unknown): value is ChartV6Response {
  if (typeof value !== "object" || value === null || !("data" in value)) return false;
  const { data } = value as { data: unknown };
  return (
    Array.isArray(data) &&
    data.every(
      (d) =>
        typeof d === "object" &&
        d !== null &&
        typeof (d as { DT?: unknown }).DT === "number" &&
        typeof (d as { Spread?: unknown }).Spread === "number",
    )
  );
}

/**
 * Accepts the fixture's flat `[timestamp, value][]` (also what a CSV export
 * parses into — `ApiTransport` hands CSV responses back as raw text, since
 * turning that into `SpreadPoint[]` is this service's concern, not the
 * transport's) or the real chartv6 `{ data: [{DT, Spread}] }` envelope.
 */
function parseSpreadResponse(raw: unknown, url: string): SpreadPoint[] {
  const data = typeof raw === "string" ? parseNumericCsv(raw) : raw;

  if (Array.isArray(data) && data.every(isSpreadPoint)) {
    return data;
  }
  if (isChartV6Response(data)) {
    return data.data.map(({ DT, Spread }): SpreadPoint => [DT, Spread]);
  }
  throw new Error(`Unexpected spread data response shape from ${url}`);
}

/**
 * One request per URL per page load, shared by every caller — so a
 * prefetch started at page load (see `prefetchTemplateSpreadCharts`) is
 * picked up by the chart that later asks for the same URL instead of
 * fetching it again. Daily spread averages don't change within a visit.
 * A failed request is evicted so the next caller retries.
 */
const requestCache = new Map<string, Promise<SpreadPoint[]>>();

/**
 * Fetches + validates a spread-average series from an arbitrary URL (e.g. a
 * `src` attribute supplied directly to `<lmax-web-spread-chart>`). Still
 * goes through `apiTransport`, so `?mock=1` swaps in the fixture regardless
 * of which URL was requested. `headers` are merged in on top of the
 * transport's defaults (e.g. the chart server's `x-lmax-chartkey`).
 */
export function fetchSpreadDataFrom(
  url: string,
  headers?: Record<string, string>,
): Promise<SpreadPoint[]> {
  const cached = requestCache.get(url);
  if (cached) return cached;

  const request = apiTransport
    .makeAPICall<unknown>(url, apiTransport.getOptions("GET", headers))
    .then((raw) => parseSpreadResponse(raw, url));
  requestCache.set(url, request);
  request.catch(() => requestCache.delete(url));
  return request;
}

/**
 * Fetches the spread-average series for a venue + symbol from the chart
 * server configured in `config.json` (`chartEndpoint` + `venues.*.venue` —
 * see `ConfigHelper.getChartUrl`). Throws if the current environment
 * doesn't have `chartEndpoint` configured (only `dev` does today).
 */
export function fetchSpreadData(
  venueKey: string,
  symbol: string,
  interval = "1d",
): Promise<SpreadPoint[]> {
  const url = configHelper.getChartUrl(venueKey, symbol, "spread_avg", interval);
  if (!url) {
    throw new Error(
      `No chart endpoint configured for venue "${venueKey}" in the "${configHelper.getEnvironment()}" environment`,
    );
  }
  return fetchSpreadDataFrom(url, configHelper.getChartHeaders());
}
